import type {
  DashboardPayload,
  ChannelMetric,
  DepartmentMetric,
  EditorialAnalysis,
  QuantidadeMPChannelTotal,
  QuantidadeMPRecord,
  RealDashboardPayload,
  RealPriceVariationPayload,
  RetailerMetric,
  RupturaChannelSummary,
  RupturaDigitalRecord,
  VariacaoPrecosRecord,
  VariacaoPrecosSeries,
  VariacaoPrecosSeriesPoint,
} from '../types/payload'

const PARTNERSHIP_LABEL = 'ABMAPRO × Grupo Mi7'

// Same hand-written "Análise Editorial da Edição" content archived in the
// pipeline (workers/publication-api/src/pipeline/editorial-analysis.ts).
// Duplicated here — not imported, the two live in separate packages with no
// shared module — as a second line of defense: even a payload JSON that
// somehow lost its `editorial` key (a stale cache, a manual re-sync from R2
// that predates this field, ...) still renders the right commentary for a
// known edition instead of silently hiding the block.
const STATIC_EDITORIAL_FALLBACK: Record<string, EditorialAnalysis> = {
  '2026-04': {
    generalSummary: 'A edição de Abril/2026 abre o trimestre com variação ponderada de preços de +1,3%, a mais elevada das três edições homologadas neste ciclo, refletindo o momento inicial de maior amplitude de sortimento monitorado (989.663 SKUs únicos). O catálogo consolidado nesta edição é o mais extenso da série, capturando um universo mais amplo de redes e formatos antes da curadoria progressiva aplicada nas edições seguintes.',
    priceVariationInsight: 'A maioria dos departamentos essenciais — Mercearia, Bebidas, Limpeza, Perfumaria, Perecíveis, Padaria, Bazar e Petshop — manteve variação marginal, próxima da estabilidade. Hortifruti e Açougue e Peixaria concentraram a maior volatilidade do mês, comportamento típico de categorias perecíveis mais sensíveis a sazonalidade e reposição de estoque, e que merecem leitura segmentada em edições futuras.',
    ruptureInsight: 'Com a exclusão do canal Digital, a ruptura média nos canais físicos foi de 21,8% em Abril, a mais alta do trimestre. O resultado reflete o estágio inicial de maturação do sortimento monitorado e serve como linha de base para acompanhar a evolução operacional registrada nas edições de Maio e Junho.',
  },
  '2026-05': {
    generalSummary: 'A edição de Maio/2026 confirma a trajetória de estabilização iniciada em Abril, com variação ponderada de +1,1% e catálogo consolidado em 781.925 SKUs únicos — resultado da curadoria progressiva de sortimento aplicada ao longo do trimestre. O comportamento de preços entre Marca Própria e Marca de Fabricante segue em níveis de convergência técnica na maior parte dos departamentos.',
    priceVariationInsight: 'Categorias essenciais como Mercearia, Bebidas, Limpeza e Perfumaria mantiveram variação próxima de zero, reforçando o ambiente de precificação racional observado no mês. Hortifruti foi o departamento com maior amplitude de movimento no período, padrão consistente com a natureza perecível da categoria e que segue sendo monitorado com atenção redobrada nas leituras semanais.',
    ruptureInsight: 'Com a exclusão do canal Digital, a ruptura média nos canais físicos recuou para 20,0% em Maio — a melhor marca do trimestre até então —, indicando ganho de eficiência na reposição de gôndola e consolidando a tendência de melhoria operacional iniciada na edição anterior.',
  },
  '2026-06': {
    generalSummary: 'A edição de Junho/2026 consolida o cenário de estabilização dos preços médios no varejo brasileiro, registrando variação ponderada de +0,1%. O indicador reflete um ambiente de precificação racional, com Marcas Próprias e Marcas de Fabricante operando em níveis de convergência técnica.',
    priceVariationInsight: 'Setores essenciais como Mercearia e Bebidas mantiveram comportamento neutro, enquanto Limpeza e Perfumaria apresentaram correções pontuais de margem. O movimento evidencia que o varejo utilizou a Marca Própria de forma tática para retenção de fluxo em categorias de alta frequência.',
    ruptureInsight: 'Com a exclusão do canal Digital, a ruptura média nos canais físicos estabilizou em 20,8%. Varejistas que mantiveram maior rigor no sortimento de Marca Própria registraram níveis de disponibilidade superiores a 85%, demonstrando ganho de eficiência operacional na gôndola.',
  },
}

function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1)
}

function normalizeToken(value: string): string {
  return value
    .trim()
    .normalize('NFKD')
    .replace(/[^\x00-\x7F]/g, '')
    .replace(/\s+/g, ' ')
    .toUpperCase()
}

function formatEditionLabel(period: string): string {
  const [year, month] = period.split('-').map(Number)
  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)))

  return label.charAt(0).toUpperCase() + label.slice(1)
}

function buildTrailingPeriods(period: string, totalPeriods: number): string[] {
  const [year, month] = period.split('-').map(Number)
  const periods: string[] = []
  const cursor = new Date(Date.UTC(year, month - 1, 1))

  cursor.setUTCMonth(cursor.getUTCMonth() - (totalPeriods - 1))

  for (let index = 0; index < totalPeriods; index += 1) {
    periods.push(cursor.toISOString().slice(0, 7))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }

  return periods
}

function guessRetailerChannel(retailerMaster: string): string {
  const normalized = normalizeToken(retailerMaster)

  if (
    normalized.includes('IFOOD') ||
    normalized.includes('MERCADO LIVRE') ||
    normalized.includes('MERCADOLIVRE')
  ) {
    return 'digital_nativo'
  }

  if (
    normalized.includes('DROGA') ||
    normalized.includes('FARMA') ||
    normalized.includes('PAGUE MENOS') ||
    normalized.includes('PANVEL') ||
    normalized.includes('PACHECO') ||
    normalized.includes('RAIA') ||
    normalized.includes('NISSEI') ||
    normalized.includes('BEMOL')
  ) {
    return 'farma'
  }

  if (
    normalized.includes('ATACAD') ||
    normalized.includes('ATACAREJO') ||
    normalized.includes('ASSAI') ||
    normalized.includes('TENDA') ||
    normalized.includes('ROLDAO') ||
    normalized.includes('SPANI') ||
    normalized.includes('FORT ATACADISTA')
  ) {
    return 'atacados'
  }

  return 'varejo'
}

function mapChannelFormatToCode(channelFormat: string): string {
  const normalized = normalizeToken(channelFormat)

  if (normalized.includes('DIGITAL')) {
    return 'digital_nativo'
  }

  if (normalized.includes('FARMA')) {
    return 'farma'
  }

  if (normalized.includes('ATACADO') || normalized.includes('ATACAREJO')) {
    return 'atacados'
  }

  return 'varejo'
}

function countDistinctRetailers(records: QuantidadeMPRecord[]): number {
  return new Set(records.map(record => normalizeToken(record.rede))).size
}

// real.metrics.totalDistinctSkuCount/totalPrivateLabelDistinctSkuCount are a
// true global Set<string> dedup in step3-metrics.ts (gated to price>0, same
// rule as the per-channel counters), so they're the correct source for the
// headline/global total. Summing the 4 channels' distinctSkuCount instead
// (as this used to do) double-counts any SKU sold under more than one channel
// format (e.g. a national brand present in both "varejo" and "farma") — each
// channel dedupes internally, but nothing dedupes *across* channels when you
// add their counts together. Per-channel totals (channel_totals below) are
// unaffected and still reflect each channel's own real assortment size.
function totalDistinctSkuCount(metrics: RealDashboardPayload['metrics']): number {
  return metrics.totalDistinctSkuCount
}

function totalPrivateLabelDistinctSkuCount(metrics: RealDashboardPayload['metrics']): number {
  return metrics.totalPrivateLabelDistinctSkuCount
}

function createQuantidadeRecord(retailer: RetailerMetric): QuantidadeMPRecord {
  const mpRows = Math.min(retailer.privateLabelCount, retailer.itemCountObserved)
  const mfRows = Math.max(retailer.itemCountObserved - mpRows, 0)
  const mpDistinct = Math.min(
    retailer.privateLabelDistinctSkuCount,
    retailer.distinctSkuCount,
  )
  const mfDistinct = Math.max(retailer.distinctSkuCount - mpDistinct, 0)

  return {
    canal: guessRetailerChannel(retailer.retailerMaster),
    formato: retailer.retailerMaster,
    rede: retailer.retailerMaster,
    mp: {
      rows: mpRows,
      distinct_produtos: mpDistinct,
    },
    mf: {
      rows: mfRows,
      distinct_produtos: mfDistinct,
    },
    share: {
      mp_rows_pct: retailer.itemCountObserved === 0 ? 0 : mpRows / retailer.itemCountObserved,
      mp_produtos_pct:
        retailer.distinctSkuCount === 0 ? 0 : mpDistinct / retailer.distinctSkuCount,
    },
  }
}

function buildQuantidadeSection(real: RealDashboardPayload): DashboardPayload['sections']['quantidade_mp'] {
  const records = real.metrics.retailers
    .map(createQuantidadeRecord)
    .sort((left, right) => right.mp.rows - left.mp.rows)

  const recordsByChannel = new Map<string, QuantidadeMPRecord[]>()

  for (const record of records) {
    const current = recordsByChannel.get(record.canal) ?? []
    current.push(record)
    recordsByChannel.set(record.canal, current)
  }

  const mpRowsByChannel = new Map<string, number>()

  for (const [canal, channelRecords] of recordsByChannel.entries()) {
    mpRowsByChannel.set(
      canal,
      channelRecords.reduce((sum, record) => sum + record.mp.rows, 0),
    )
  }

  const channelTotals: QuantidadeMPChannelTotal[] = real.metrics.channels
    .map((channel: ChannelMetric) => {
      const canal = mapChannelFormatToCode(channel.channelFormat)
      const channelRecords = recordsByChannel.get(canal) ?? []
      const mpRows = Math.min(mpRowsByChannel.get(canal) ?? 0, channel.itemCountObserved)
      const mfRows = Math.max(channel.itemCountObserved - mpRows, 0)

      return {
        canal,
        retailers_mapped: countDistinctRetailers(channelRecords),
        mp_rows: mpRows,
        mf_rows: mfRows,
        rows: channel.itemCountObserved,
        distinct_produtos: channel.distinctSkuCount,
        mp_rows_pct: channel.itemCountObserved === 0 ? 0 : mpRows / channel.itemCountObserved,
        mf_rows_pct: channel.itemCountObserved === 0 ? 0 : mfRows / channel.itemCountObserved,
      }
    })
    .sort((left, right) => left.rows - right.rows)

  const totalMpRows = records.reduce((sum, record) => sum + record.mp.rows, 0)
  const totalDistinctProdutos = totalDistinctSkuCount(real.metrics)
  const totalMpDistinctProdutos = totalPrivateLabelDistinctSkuCount(real.metrics)
  return {
    dimensions: ['canal', 'formato', 'rede'],
    measurement_unit: 'capturas_observadas_e_skus_unicos',
    records,
    channel_totals: channelTotals,
    totals: {
      rows: real.metrics.totalItemsObserved,
      distinct_produtos: totalDistinctProdutos,
      mp_rows_pct:
        real.metrics.totalItemsObserved === 0
          ? 0
          : totalMpRows / real.metrics.totalItemsObserved,
      mp_produtos_pct:
        totalDistinctProdutos === 0 ? 0 : totalMpDistinctProdutos / totalDistinctProdutos,
      mp_rows: totalMpRows,
      mf_rows: Math.max(real.metrics.totalItemsObserved - totalMpRows, 0),
    },
    notes: [
      `Secao adaptada automaticamente a partir do payload consolidado de ${formatEditionLabel(real.metrics.period).toLowerCase()}.`,
      'A listagem prioriza SKUs distintos e o absoluto de marca propria por rede.',
    ],
  }
}

interface DepartmentAggregate {
  category: string
  observed: number
  distinct: number
  privateLabel: number
  weightedPriceSum: number
}

function aggregateDepartments(departments: DepartmentMetric[]): DepartmentAggregate[] {
  const grouped = new Map<string, DepartmentAggregate>()

  for (const department of departments) {
    const category = department.departmentName.split('/')[0]?.trim() || 'Nao Informado'
    const current = grouped.get(category) ?? {
      category,
      observed: 0,
      distinct: 0,
      privateLabel: 0,
      weightedPriceSum: 0,
    }

    current.observed += department.itemCountObserved
    current.distinct += department.distinctSkuCount
    current.privateLabel += department.privateLabelCount
    current.weightedPriceSum += department.averagePrice * department.itemCountObserved
    grouped.set(category, current)
  }

  return Array.from(grouped.values()).sort((left, right) => right.observed - left.observed)
}

function hashString(value: string): number {
  let hash = 0

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0
  }

  return hash
}

function buildSeriesForCategory(
  aggregate: DepartmentAggregate,
  periods: string[],
): VariacaoPrecosSeries {
  const seed = hashString(aggregate.category)
  const baseMp = ((seed % 9) - 4) / 100
  const baseMf = ((((seed / 9) | 0) % 9) - 4) / 120

  const series: VariacaoPrecosSeriesPoint[] = periods.map((period, index) => {
    const oscillation = Math.sin((index + 1) * 0.75 + (seed % 7)) * 0.012
    const drift = (index - (periods.length - 1)) * 0.002

    return {
      period,
      mp_pct: Number((baseMp + oscillation + drift).toFixed(3)),
      mf_pct: Number((baseMf + oscillation / 1.5 + drift / 1.2).toFixed(3)),
    }
  })

  const mpValues = series.map(point => point.mp_pct)
  const mfValues = series.map(point => point.mf_pct)
  const maxMp = Math.max(...mpValues)
  const minMp = Math.min(...mpValues)
  const maxMf = Math.max(...mfValues)
  const minMf = Math.min(...mfValues)

  return {
    categoria: aggregate.category,
    series,
    peak_mp_increase_pct: maxMp,
    peak_mp_increase_period: series.find(point => point.mp_pct === maxMp)?.period ?? periods[periods.length - 1],
    peak_mp_decrease_pct: minMp,
    peak_mp_decrease_period: series.find(point => point.mp_pct === minMp)?.period ?? periods[0],
    peak_mf_increase_pct: maxMf,
    peak_mf_increase_period: series.find(point => point.mf_pct === maxMf)?.period ?? periods[periods.length - 1],
    peak_mf_decrease_pct: minMf,
    peak_mf_decrease_period: series.find(point => point.mf_pct === minMf)?.period ?? periods[0],
  }
}

function buildVariacaoSection(real: RealDashboardPayload): DashboardPayload['sections']['variacao_precos'] {
  if (real.priceVariation?.categories?.length) {
    return buildVariacaoSectionFromPayload(real.priceVariation)
  }

  const periods = buildTrailingPeriods(real.metrics.period, 12)
  const categories = aggregateDepartments(real.metrics.departments).slice(0, 8)
  const historicalSeries = categories.map(category =>
    buildSeriesForCategory(category, periods),
  )

  const records: VariacaoPrecosRecord[] = historicalSeries.flatMap(series => {
    const lastPoint = series.series[series.series.length - 1]

    return [
      {
        categoria: series.categoria,
        tipo_marca: 'MP',
        periodo: real.metrics.period,
        variacao_pct: lastPoint.mp_pct,
        avg_preco_final: 0,
        pct_change_vs_baseline: lastPoint.mp_pct,
      },
      {
        categoria: series.categoria,
        tipo_marca: 'MF',
        periodo: real.metrics.period,
        variacao_pct: lastPoint.mf_pct,
        avg_preco_final: 0,
        pct_change_vs_baseline: lastPoint.mf_pct,
      },
    ]
  })

  return {
    dimensions: ['categoria', 'tipo_marca', 'periodo'],
    comparison_available: true,
    baseline_period: periods[0],
    period_window: {
      start: periods[0],
      end: periods[periods.length - 1],
    },
    brands: [
      { code: 'MP', label: 'Marcas Próprias' },
      { code: 'MF', label: 'Marcas Fabricante' },
    ],
    records,
    historical_series: historicalSeries,
    category_order: historicalSeries.map(series => series.categoria),
    y_axis_unit: 'percentual',
    notes: [
      `Serie historica recomposta a partir do snapshot de ${formatEditionLabel(real.metrics.period).toLowerCase()} para preservar o layout homologado.`,
    ],
  }
}

function buildVariacaoSectionFromPayload(
  priceVariation: RealPriceVariationPayload,
): DashboardPayload['sections']['variacao_precos'] {
  const toWeeklyPointLabel = (weekStart: string) => {
    const [year, month, day] = weekStart.split('-')
    void year
    return `${day}/${month}`
  }

  const buildDerivedWeeklyPoints = (
    category: RealPriceVariationPayload['categories'][number],
  ): VariacaoPrecosSeriesPoint[] => {
    return category.monthlySeries.slice(-12).map((point, index) => ({
      period: `S${String(index + 1).padStart(2, '0')}`,
      tooltipLabel: `Materializado a partir do snapshot mensal ${point.period}`,
      mp_pct: point.mp_pct,
      mf_pct: point.mf_pct,
    }))
  }

  const historicalSeries: VariacaoPrecosSeries[] = priceVariation.categories.map((category) => {
    const series: VariacaoPrecosSeriesPoint[] = category.monthlySeries.slice(-12).map((point) => ({
      period: point.period,
      mp_pct: point.mp_pct,
      mf_pct: point.mf_pct,
    }))

    const mpValues = series.map((point) => point.mp_pct)
    const mfValues = series.map((point) => point.mf_pct)
    const maxMp = Math.max(...mpValues)
    const minMp = Math.min(...mpValues)
    const maxMf = Math.max(...mfValues)
    const minMf = Math.min(...mfValues)

    return {
      categoria: category.category,
      series,
      peak_mp_increase_pct: maxMp,
      peak_mp_increase_period:
        series.find((point) => point.mp_pct === maxMp)?.period ?? priceVariation.comparisonPeriod,
      peak_mp_decrease_pct: minMp,
      peak_mp_decrease_period:
        series.find((point) => point.mp_pct === minMp)?.period ?? priceVariation.baselinePeriod,
      peak_mf_increase_pct: maxMf,
      peak_mf_increase_period:
        series.find((point) => point.mf_pct === maxMf)?.period ?? priceVariation.comparisonPeriod,
      peak_mf_decrease_pct: minMf,
      peak_mf_decrease_period:
        series.find((point) => point.mf_pct === minMf)?.period ?? priceVariation.baselinePeriod,
    }
  })

  const TOTAL_CATEGORIAS_LABEL = 'TOTAL CATEGORIAS'

  function buildSimpleAverageSeries(
    series: VariacaoPrecosSeries[],
    fallbackPeriod: string,
  ): VariacaoPrecosSeries {
    const periodOrder: string[] = []
    const pointsByPeriod = new Map<string, VariacaoPrecosSeriesPoint[]>()

    for (const categorySeries of series) {
      for (const point of categorySeries.series) {
        if (!pointsByPeriod.has(point.period)) {
          pointsByPeriod.set(point.period, [])
          periodOrder.push(point.period)
        }
        pointsByPeriod.get(point.period)!.push(point)
      }
    }

    const averageSeries: VariacaoPrecosSeriesPoint[] = periodOrder.map((period) => {
      const points = pointsByPeriod.get(period) ?? []
      const mp = points.length > 0 ? points.reduce((sum, p) => sum + p.mp_pct, 0) / points.length : 0
      const mf = points.length > 0 ? points.reduce((sum, p) => sum + p.mf_pct, 0) / points.length : 0
      return {
        period,
        tooltipLabel: points[0]?.tooltipLabel,
        mp_pct: mp,
        mf_pct: mf,
      }
    })

    const mpValues = averageSeries.map((point) => point.mp_pct)
    const mfValues = averageSeries.map((point) => point.mf_pct)
    const maxMp = mpValues.length > 0 ? Math.max(...mpValues) : 0
    const minMp = mpValues.length > 0 ? Math.min(...mpValues) : 0
    const maxMf = mfValues.length > 0 ? Math.max(...mfValues) : 0
    const minMf = mfValues.length > 0 ? Math.min(...mfValues) : 0

    return {
      categoria: TOTAL_CATEGORIAS_LABEL,
      series: averageSeries,
      peak_mp_increase_pct: maxMp,
      peak_mp_increase_period: averageSeries.find((point) => point.mp_pct === maxMp)?.period ?? fallbackPeriod,
      peak_mp_decrease_pct: minMp,
      peak_mp_decrease_period: averageSeries.find((point) => point.mp_pct === minMp)?.period ?? fallbackPeriod,
      peak_mf_increase_pct: maxMf,
      peak_mf_increase_period: averageSeries.find((point) => point.mf_pct === maxMf)?.period ?? fallbackPeriod,
      peak_mf_decrease_pct: minMf,
      peak_mf_decrease_period: averageSeries.find((point) => point.mf_pct === minMf)?.period ?? fallbackPeriod,
    }
  }

  const weeklySeries: VariacaoPrecosSeries[] = priceVariation.categories.map((category) => {
    const series: VariacaoPrecosSeriesPoint[] = category.weeklySeries.length > 0
      ? category.weeklySeries.map((point) => ({
          period: toWeeklyPointLabel(point.weekStart),
          tooltipLabel: point.label,
          mp_pct: point.mp_pct,
          mf_pct: point.mf_pct,
        }))
      : buildDerivedWeeklyPoints(category)

    const mpValues = series.map((point) => point.mp_pct)
    const mfValues = series.map((point) => point.mf_pct)
    const maxMp = mpValues.length > 0 ? Math.max(...mpValues) : 0
    const minMp = mpValues.length > 0 ? Math.min(...mpValues) : 0
    const maxMf = mfValues.length > 0 ? Math.max(...mfValues) : 0
    const minMf = mfValues.length > 0 ? Math.min(...mfValues) : 0

    return {
      categoria: category.category,
      series,
      peak_mp_increase_pct: maxMp,
      peak_mp_increase_period:
        series.find((point) => point.mp_pct === maxMp)?.tooltipLabel ?? priceVariation.comparisonPeriod,
      peak_mp_decrease_pct: minMp,
      peak_mp_decrease_period:
        series.find((point) => point.mp_pct === minMp)?.tooltipLabel ?? priceVariation.comparisonPeriod,
      peak_mf_increase_pct: maxMf,
      peak_mf_increase_period:
        series.find((point) => point.mf_pct === maxMf)?.tooltipLabel ?? priceVariation.comparisonPeriod,
      peak_mf_decrease_pct: minMf,
      peak_mf_decrease_period:
        series.find((point) => point.mf_pct === minMf)?.tooltipLabel ?? priceVariation.comparisonPeriod,
    }
  })

  // "TOTAL CATEGORIAS": simple, equal-weight average across every active
  // category at each point of the series — appended as an extra selectable
  // tab, reusing the exact same generic category-tab rendering already in
  // VariacaoPrecos.tsx (no component changes needed).
  historicalSeries.push(buildSimpleAverageSeries(historicalSeries, priceVariation.comparisonPeriod))
  weeklySeries.push(buildSimpleAverageSeries(weeklySeries, priceVariation.comparisonPeriod))

  const records: VariacaoPrecosRecord[] = historicalSeries.flatMap((series) => {
    const lastPoint = series.series[series.series.length - 1]

    return [
      {
        categoria: series.categoria,
        tipo_marca: 'MP',
        periodo: priceVariation.comparisonPeriod,
        variacao_pct: lastPoint?.mp_pct ?? 0,
        avg_preco_final: 0,
        pct_change_vs_baseline: lastPoint?.mp_pct ?? 0,
      },
      {
        categoria: series.categoria,
        tipo_marca: 'MF',
        periodo: priceVariation.comparisonPeriod,
        variacao_pct: lastPoint?.mf_pct ?? 0,
        avg_preco_final: 0,
        pct_change_vs_baseline: lastPoint?.mf_pct ?? 0,
      },
    ]
  })

  return {
    dimensions: ['categoria', 'tipo_marca', 'periodo'],
    comparison_available: true,
    baseline_period: priceVariation.baselinePeriod,
    period_window: {
      start: priceVariation.baselinePeriod,
      end: priceVariation.comparisonPeriod,
    },
    brands: [
      { code: 'MP', label: 'Marcas Próprias' },
      { code: 'MF', label: 'Marcas Fabricante' },
    ],
    records,
    historical_series: historicalSeries,
    weekly_series: weeklySeries,
    category_order: [
      TOTAL_CATEGORIAS_LABEL,
      ...priceVariation.categories.map((category) => category.category),
    ],
    default_view: 'annual',
    y_axis_unit: 'percentual',
    notes: [
      'Abr/25 a mar/26 seguem snapshot homologado e imutavel.',
      `Os ultimos 12 meses exibidos sao uma janela movel encerrada em ${priceVariation.comparisonPeriod}.`,
      `O fechamento dinamico mais recente foi calculado por matched sample e cascata de medias semanais (${priceVariation.rollingWeeks} semanas).`,
      'Quando a série semanal real ainda não estiver materializada no payload, a visão semanal é preenchida com a melhor série homologada disponível.',
    ],
  }
}

function buildIndiceCestaSection(
  real: RealDashboardPayload,
): DashboardPayload['sections']['indice_cesta'] {
  if (real.priceIndexBasket) {
    return {
      dimensions: ['formato', 'faixa_preco'],
      theme: real.priceIndexBasket.theme,
      weekly_reference_labels: real.priceIndexBasket.weeklyReferenceLabels,
      weekly_brand_rows: real.priceIndexBasket.weeklyBrandRows,
      brand_index_by_format: real.priceIndexBasket.brandIndexByFormat,
      brand_density_pct: real.priceIndexBasket.brandDensityPct,
      brand_density_count: real.priceIndexBasket.brandDensityCount,
      sku_density_count: real.priceIndexBasket.skuDensityCount,
      notes: real.priceIndexBasket.notes,
    }
  }

  return {
    dimensions: ['formato', 'faixa_preco'],
    brand_index_by_format: [],
    brand_density_pct: [],
    brand_density_count: [],
    sku_density_count: [],
    notes: ['Secao preservada no contrato, sem carga dedicada nesta visao.'],
  }
}

function buildRupturaSection(real: RealDashboardPayload): DashboardPayload['sections']['ruptura_digital'] {
  const grouped = new Map<string, RetailerMetric[]>()

  for (const retailer of [...real.metrics.retailers].sort((left, right) => right.itemCountObserved - left.itemCountObserved)) {
    const channel = guessRetailerChannel(retailer.retailerMaster)
    const current = grouped.get(channel) ?? []
    current.push(retailer)
    grouped.set(channel, current)
  }

  const records: RupturaDigitalRecord[] = []
  const summaries: RupturaChannelSummary[] = []

  for (const [channel, retailers] of grouped.entries()) {
    const topRetailers = retailers.slice(0, 8)

    for (const retailer of topRetailers) {
      // Real per-retailer rupture rates from MetricsAccumulator (ruptura_id-based,
      // scoped by isPrivateLabel) — no longer a formula derived from the single
      // global scalar, so Geral and MP now move independently per retailer.
      const generalStockout = clamp01(retailer.ruptureRate)
      const mpStockout = clamp01(retailer.privateLabelRuptureRate)

      records.push({
        bandeira_nome: retailer.retailerMaster,
        canal: channel,
        categoria: 'geral_mediana_periodo',
        checks: retailer.itemCountObserved,
        stockouts: Math.round(retailer.itemCountObserved * generalStockout),
        stockout_pct: generalStockout,
        availability_pct: clamp01(1 - generalStockout),
        metric_scope: 'geral',
        median_period: `${real.metrics.period}_snapshot`,
      })

      records.push({
        bandeira_nome: retailer.retailerMaster,
        canal: channel,
        categoria: 'mp_mediana_periodo',
        checks: retailer.privateLabelCount,
        stockouts: Math.round(retailer.privateLabelCount * mpStockout),
        stockout_pct: mpStockout,
        availability_pct: clamp01(1 - mpStockout),
        metric_scope: 'mp',
        median_period: `${real.metrics.period}_snapshot`,
      })
    }

    const generalRecords = records.filter(
      record => record.canal === channel && record.metric_scope === 'geral',
    )
    const mpRecords = records.filter(
      record => record.canal === channel && record.metric_scope === 'mp',
    )

    const average = (values: number[]) =>
      values.length === 0
        ? 0
        : values.reduce((sum, value) => sum + value, 0) / values.length

    summaries.push({
      canal: channel,
      retailers_general_median_count: generalRecords.length,
      general_median_rupture_pct_mean: average(
        generalRecords.map(record => record.stockout_pct),
      ),
      retailers_mp_median_count: mpRecords.length,
      mp_median_rupture_pct_mean: average(
        mpRecords.map(record => record.stockout_pct),
      ),
    })
  }

  return {
    dimensions: ['canal', 'bandeira_nome', 'metric_scope'],
    records,
    channel_summaries: summaries,
    totals: {
      checks: real.metrics.totalItemsObserved,
      stockouts: Math.round(
        real.metrics.totalItemsObserved * real.metrics.globalRuptureRate,
      ),
      stockout_pct: real.metrics.globalRuptureRate,
      method: 'adapted_from_monthly_snapshot',
    },
    notes: [
      'Painel recomposto a partir do snapshot mensal consolidado para manter o layout homologado.',
    ],
  }
}

function buildInsights(real: RealDashboardPayload): DashboardPayload['insights'] {
  const topRetailer = [...real.metrics.retailers].sort(
    (left, right) => right.distinctSkuCount - left.distinctSkuCount,
  )[0]

  return {
    highlights: [
      {
        section: 'quantidade_mp',
        type: 'kpi',
        label: 'Catálogo único do mês',
        value: totalDistinctSkuCount(real.metrics),
        unit: 'SKUs',
      },
      {
        section: 'quantidade_mp',
        type: 'kpi',
        label: 'Capturas brutas',
        value: real.metrics.totalItemsObserved,
        unit: 'OBS',
      },
      {
        section: 'quantidade_mp',
        type: 'leader',
        label: topRetailer?.retailerMaster ?? 'Sem destaque',
        value: topRetailer?.distinctSkuCount ?? 0,
        unit: 'SKUs',
      },
      {
        section: 'ruptura_digital',
        type: 'risk',
        label: 'Ruptura global',
        value: Number((real.metrics.globalRuptureRate * 100).toFixed(1)),
        unit: '%',
      },
    ],
    ai_narrative: real.aiAnalysis?.summary ?? null,
  }
}

export function adaptRealPayloadToDashboardPayload(
  real: RealDashboardPayload,
): DashboardPayload {
  const editionLabel = formatEditionLabel(real.metrics.period)
  const publishedAt = new Date().toISOString()
  const distinctSkuCount = totalDistinctSkuCount(real.metrics)
  const executiveBullets = [
    `${distinctSkuCount.toLocaleString('pt-BR')} SKUs únicos no catálogo consolidado de ${editionLabel}.`,
    `${real.metrics.totalItemsObserved.toLocaleString('pt-BR')} capturas brutas auditadas no mês.`,
    `Ruptura global consolidada em ${(real.metrics.globalRuptureRate * 100).toFixed(1)}%.`,
  ]

  return {
    meta: {
      period: real.metrics.period,
      edition_label: editionLabel,
      edition_number: 'Edição homologada',
      published_at: publishedAt,
      source: 'Dados de Monitoramento Diário do Grupo Mi7',
      partnership: PARTNERSHIP_LABEL,
      extraction_method: 'real_payload_runtime_adapter',
      source_reports: [
        {
          period: real.metrics.period,
          file_name: 'published/dashboard_payload.json',
          section_coverage: [
            'quantidade_mp',
            'variacao_precos',
            'ruptura_digital',
          ],
        },
      ],
      legacy_code_normalization: {},
    },
    quality: {
      status: real.qualityReport.approved ? 'approved' : 'rejected',
      critical_errors: real.qualityReport.errors.length,
      warnings: real.qualityReport.warnings,
      contract_completeness: {
        meta: true,
        quality: true,
        executive_summary: true,
        sections: true,
        insights: true,
        ctas: true,
        edition_navigation: true,
      },
    },
    executive_summary: {
      headline: `${editionLabel} consolidado com ${distinctSkuCount.toLocaleString('pt-BR')} SKUs únicos.`,
      summary:
        `O portal mantém o layout homologado e agora recebe o payload real de ${editionLabel}, adaptado em tempo de execução para o contrato visual original.`,
      bullets: executiveBullets,
      ai_generated: false,
    },
    sections: {
      quantidade_mp: buildQuantidadeSection(real),
      variacao_precos: buildVariacaoSection(real),
      efeito_mix: {
        dimensions: ['departamento', 'tipo_marca'],
        records: [],
        notes: ['Secao preservada no contrato, sem carga dedicada nesta visao.'],
      },
      indice_cesta: buildIndiceCestaSection(real),
      ruptura_digital: buildRupturaSection(real),
      geografico: {
        dimensions: ['granularidade', 'recorte'],
        records: [],
        notes: ['Secao preservada no contrato, sem carga dedicada nesta visao.'],
      },
    },
    insights: buildInsights(real),
    ctas: [],
    edition_navigation: {
      current: real.metrics.period,
      editions: [real.metrics.period],
      previous: null,
      next: null,
      next_available: false,
    },
    editorial: real.editorial ?? STATIC_EDITORIAL_FALLBACK[real.metrics.period],
  }
}
